import { usePatient } from '@/hooks/usePatients'
import { formatDate, formatSex, patientInitials, patientName } from '@/lib/patient'

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className='min-w-0'>
      <p className='text-[10px] font-medium uppercase tracking-wider text-gray-400'>{label}</p>
      <p className='mt-0.5 whitespace-nowrap text-sm font-medium text-gray-800'>{value}</p>
    </div>
  )
}

/**
 * Sticky patient identity header, shared by the detail, procedure and form-renderer pages.
 *
 * Reads the real Patient record via `usePatient`. This used to be a hardcoded map of
 * five MRNs, so every name on screen was a fixture and any other patient rendered
 * "Patient data unavailable". Diagnosis, blood type and physician came from that same
 * fixture and have no backing field, so they are gone rather than permanently blank.
 *
 * All three host pages share the query key, so the extra fetch is one cached request.
 */
export function PatientBanner({
  mrn,
  trail,
  displayName,
  hidePatient = false,
}: {
  mrn: string
  /**
   * Optional inline navigation trail (breadcrumbs, procedure date, status pill).
   * When set, the banner collapses to one compact row: the MRN subtitle and
   * the "data unavailable" filler are dropped (the trail already carries the
   * MRN), merging what used to be two stacked header rows.
   */
  trail?: React.ReactNode
  /**
   * Name derived from form submissions. Used only as a fallback for a patient whose
   * record carries no name, so a chart-form name still beats showing the bare MRN.
   */
  displayName?: string | null
  /**
   * Render the trail alone, with no person on it.
   *
   * A KIT run is the same procedure without a patient, so it reuses this
   * banner for the trail (crumbs, started, status, assignee) but must not show
   * an avatar, demographics or an "Active Patient" badge for someone who does
   * not exist.
   */
  hidePatient?: boolean
}) {
  const { data: patient } = usePatient(hidePatient ? '' : mrn)

  const resolvedName = (patient ? patientName(patient) : null) ?? displayName ?? null
  const initials = patientInitials(resolvedName, mrn)
  const fullName = resolvedName ?? mrn

  const dob = formatDate(patient?.birthDate)
  const sex = formatSex(patient?.sex)
  const age = patient?.age
  const hasDemographics = Boolean(dob || sex || age != null)

  // z-30, not z-10: content scrolls under this banner, and table forms pin
  // their first column (z-10) and resize handles (z-20). A tie goes to
  // whatever comes later in the DOM, which is the table.
  return (
    <div
      className={`sticky top-0 z-30 border-b border-gray-200 bg-white px-6 shadow-sm ${trail ? 'py-2' : 'py-3'}`}
    >
      <div className='flex flex-wrap items-center gap-x-5 gap-y-2'>
        {!hidePatient && (
        <>
        {/* Avatar + Name */}
        <div className='flex items-center gap-3'>
          <div
            className={`flex shrink-0 items-center justify-center rounded-full bg-blue-600 font-bold text-white ${trail ? 'h-8 w-8 text-xs' : 'h-10 w-10 text-sm'}`}
          >
            {initials}
          </div>
          <div>
            <p className='font-semibold leading-tight text-gray-900'>{fullName}</p>
            {!trail && <p className='font-mono text-xs text-gray-400'>{mrn}</p>}
          </div>
        </div>

        <div className='h-8 w-px bg-gray-200' />
        </>
        )}

        {trail}

        {hidePatient ? null : hasDemographics ? (
          <>
            {trail && <div className='h-8 w-px bg-gray-100' />}
            {dob && (
              <>
                <Field label='DOB' value={dob} />
                <div className='h-8 w-px bg-gray-100' />
              </>
            )}
            {age != null && (
              <>
                <Field label='Age' value={`${age} yrs`} />
                <div className='h-8 w-px bg-gray-100' />
              </>
            )}
            {sex && <Field label='Sex' value={sex} />}
          </>
        ) : (
          !trail && <p className='text-sm text-gray-400'>No demographics on file</p>
        )}

        {!hidePatient && (
          <div className='ml-auto'>
            <span className='rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-700'>
              Active Patient
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
